/** Date and clock helpers shared by both apps. Dates are `yyyy-mm-dd` strings. */

import { clockLabel, clockRange, ddmmyy, parseDateKey } from './format';

// One implementation each, kept in format.ts; re-exported under the names the calendar reads.
export { clockLabel, clockRange, dateKeyOf as keyOf, parseDateKey as parseKey } from './format';

const pad = (n: number) => String(n).padStart(2, '0');

/** Today's `yyyy-mm-dd` key in a named timezone, independent of the viewer's device timezone. */
export const todayInTimeZone = (timeZone: string, now = new Date()) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);

/** The first of the month `n` months from `d`. Negative `n` goes backwards. */
export const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);

/** True when both Dates fall on the same calendar day. */
export const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** "September 2026" */
export const monthTitle = (d: Date) =>
  d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

const ordinal = (n: number) => {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};

/** "Mon, 8th" */
export const dayTitle = (d: Date) =>
  `${d.toLocaleDateString(undefined, { weekday: 'short' })}, ${ordinal(d.getDate())}`;

/**
 * The six-week grid for a month, starting Sunday. Always 42 cells so the
 * calendar does not change height as months change.
 */
export function monthGrid(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

/** Column headings for a Sunday-first month grid, matching `monthGrid`. */
export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/* ---------------------------------------------------------------- clock ---
 * 12-hour times throughout. These are deliberately not
 * `toLocaleTimeString` — that follows the viewer's locale and would render
 * 24-hour clocks for most of the world, which is what we are moving away from.
 * -------------------------------------------------------------------------- */

/** A whole hour to "2:00 PM". */
export const hourLabel = (hour: number) => clockLabel(hour * 60);

/** Whole-hour range, e.g. bookings stored as hour numbers. */
export const hourRange = (fromHour: number, toHour: number) =>
  clockRange(fromHour * 60, toHour * 60);

/** "18:30", the value an <input type="time"> holds, to minutes from midnight. */
export const minutesOf = (value: string) => {
  const [h, m] = value.split(':').map(Number);
  if (!Number.isFinite(h)) return 0;
  return h * 60 + (Number.isFinite(m) ? m : 0);
};

/** The inverse of `minutesOf` — 1110 back to the "18:30" a time input wants. */
export const timeValue = (minutes: number) =>
  `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

/** A bare `yyyy-mm-dd` key, as opposed to a full ISO timestamp. */
const isDateKey = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value);

/**
 * "12/09/26, 10:04 AM" for an ISO string from the API — audit rows and
 * account timestamps. Null reads as an em dash rather than "Invalid Date".
 */
export const stamp = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${ddmmyy(d)}, ${clockOf(d)}`;
};

/**
 * "12/09/26" — for an API date where the time of day carries no meaning.
 * Takes a full ISO timestamp or a bare `yyyy-mm-dd` key; a key is read as a
 * calendar date, since `new Date(key)` is UTC midnight and would show the day
 * before to anyone west of Greenwich.
 */
export const dateStamp = (value: string | null) =>
  value ? ddmmyy(isDateKey(value) ? parseDateKey(value) : new Date(value)) : '—';

/** "2:30 PM" for a Date — used for "booked at" stamps. */
export const clockOf = (d: Date) => clockLabel(d.getHours() * 60 + d.getMinutes());

/** Compact axis label — "9 AM", "12 PM". Narrower than the full "9:00 AM". */
export const hourShort = (hour: number) => {
  const h24 = ((Math.round(hour) % 24) + 24) % 24;
  return `${h24 % 12 === 0 ? 12 : h24 % 12} ${h24 < 12 ? 'AM' : 'PM'}`;
};
